import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Alert, Button, Form} from 'antd';
import {Collapse} from 'react-collapse';
import AntdModal from '../../../AntdModal';
import NumericPasswordInput from '../../../NumericPasswordInput';
import {formHelpers} from '../../../../utils/helpers';
import {validation} from '../../../../utils/validation';

const {Item} = Form;

const defaultFormValues = {
  confirm_PIN: '',
  new_PIN: '',
};

const pinLength = 4;

const SetNewPinModal = ({
  loading,
  open,
  onOk,
  onCancel,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'cards']);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);

  const inputProps = {
    maxLength: 4,
    placeholder: t('4 digits'),
    size: 'large',
  }

  const handleSubmit = (formData) => {
    if (!onOk) return;

    onOk({
      data: formData,
      errorCallback: (error) => {
        const responseData = error?.response?.data;

        if (responseData?.detail) {
          setError(responseData.detail);
        } else {
          const fields = formHelpers.getFormServerErrorFields(responseData);
          form.setFields(fields);
        }
      },
    });
  };

  const handleAfterClose = () => {
    form.setFieldsValue(defaultFormValues);
    if (error) setError(null);
  }

  const validationRules = useMemo(() => ({
    password: validation.password(t, pinLength),
    passwordConfirm: validation.passwordConfirm(t, pinLength, 'new_PIN'),
  }), [t]);

  const handleSubmitForm = () => form.submit();

  const footer = [
    <Button
      key='cancel'
      onClick={onCancel}
      size='large'
    >
      {t('cancel')}
    </Button>,
    <Button
      key='submit'
      loading={loading}
      onClick={handleSubmitForm}
      size='large'
      type='primary'
    >
      {t('submit')}
    </Button>
  ];

  return (
    <AntdModal
      afterClose={handleAfterClose}
      forceRender
      footer={footer}
      onCancel={onCancel}
      open={open}
      title={t('setNewPin')}
      width={370}
      {...rest}
    >
      <Form
        disabled={loading}
        initialValues={defaultFormValues}
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
      >
        <Item
          label='PIN'
          name='new_PIN'
          rules={validationRules.password}
          required
        >
          <NumericPasswordInput {...inputProps} />
        </Item>
        <Item
          label={`${t('confirm')} PIN`}
          name='confirm_PIN'
          rules={validationRules.passwordConfirm}
          required
        >
          <NumericPasswordInput {...inputProps} />
        </Item>

        <Collapse isOpened={error}>
          <Alert message={error} type='error' showIcon />
        </Collapse>
      </Form>

    </AntdModal>
  );
}

SetNewPinModal.propTypes = {
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onOk: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
}

export default SetNewPinModal;
